/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  type ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { AppURL } from '@poweralpha/common';
import { lowerCase } from 'lodash';
import { useCallback, useMemo } from 'react';
import { StdButton } from './buttons';
import usePANavigate from './navigate/usePANavigate';

interface Props<T = undefined> {
  onConfirm: (data: T) => Promise<void>;
  redirectPath?: AppURL;
  label: string;
  color?: ButtonProps['color'];
  name?: string;
  customConfirmationText?: string | ((data: T) => string);
  customConfirmButtonLabel?: string;
  isLoading?: boolean;
  onCancel?: () => Promise<void>;
  itemType?: string;
  connectorWord?: string;
  id?: string;
}

export interface ConfirmDialogQueryParams<T = undefined> {
  confirmDialogOpen?: boolean | string;
  refreshListing?: boolean;
  customData?: T;
}

function ConfirmDialog<T = undefined>({
  onConfirm,
  redirectPath,
  label,
  color,
  name,
  customConfirmationText,
  customConfirmButtonLabel,
  isLoading,
  onCancel,
  itemType = 'record',
  connectorWord = 'this',
  id,
}: Readonly<Props<T>>) {
  const { navigate, location } = usePANavigate<ConfirmDialogQueryParams>();

  const handleDialogClose = useCallback(() => navigate(-1), [navigate]);

  const handleCancel = useCallback(() => {
    handleDialogClose();
    onCancel?.();
  }, [handleDialogClose, onCancel]);

  const handleConfirmClick = useCallback(() => {
    onConfirm(location.state?.customData as T).then(() => {
      if (redirectPath) {
        navigate(`${redirectPath}${location.search}`, {
          confirmDialogOpen: false,
          refreshListing: true,
        });
        return;
      }
      handleDialogClose();
    });
  }, [
    onConfirm,
    navigate,
    redirectPath,
    location.search,
    location.state?.customData,
    handleDialogClose,
  ]);

  const dialogText = useMemo(() => {
    if (customConfirmationText) {
      if (typeof customConfirmationText === 'function') {
        return customConfirmationText(location.state?.customData as T);
      }
      return customConfirmationText;
    }
    return `${lowerCase(label)} ${connectorWord} ${itemType}`;
  }, [customConfirmationText, connectorWord, itemType, label, location.state?.customData]);

  const isOpen = useMemo(() => {
    const param = location.state?.confirmDialogOpen;
    if (typeof param === 'string') {
      return param === id;
    }
    return param ?? false;
  }, [location.state?.confirmDialogOpen, id]);

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={isOpen}
      onClose={handleDialogClose}
      data-testid="confirm-dialog"
    >
      <DialogTitle>Confirm {label}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to {dialogText}?
          <br />
        </DialogContentText>
        <Typography
          sx={{
            fontSize: 14,
            mt: 1,
          }}
        >
          <i>{name ?? ''}</i>
        </Typography>
      </DialogContent>
      <DialogActions>
        <StdButton label="Cancel" onClick={handleCancel} />
        <StdButton
          label={customConfirmButtonLabel ?? label}
          onClick={handleConfirmClick}
          loading={isLoading}
          variant="contained"
          color={color}
          data-testid={`confirm-dialog-confirm-button${id ? `-${id}` : ''}`}
        />
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
